// PreCropTool — modal rectangle crop for staff. Used before sending an image
// to the ML cutout pipeline. The model consistently does better with less
// background noise; pre-cropping to the pancake region eliminates griddle
// edges, table surface, etc.
//
// Touch + mouse. Drag inside the crop = move. Drag a handle = resize.
// Returns a cropped JPEG blob via onConfirm. Cancel returns null.
//
// Use:
//   <PreCropTool
//     src={imageUrl}
//     onConfirm={(blob) => ...}
//     onCancel={() => ...}
//     title="Crop to the pancake"
//     subtitle="Less background = cleaner cutout."
//   />

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const HANDLE_VISUAL = 14; // px — visual size of corner/edge dots
const HANDLE_HIT    = 36; // px — invisible hit area for fingers

const B = {
  navy:   "#0d1833",
  cream:  "#ffdaad",
  pink:   "#ef537e",
  gold:   "#fcb31f",
  sky:    "#4aa4e1",
  border: "rgba(255,218,173,0.22)",
  muted:  "rgba(255,218,173,0.6)",
};

export default function PreCropTool({ src, onConfirm, onCancel, title = "Crop the image", subtitle = "" }) {
  const containerRef = useRef(null);
  const imgRef       = useRef(null);
  const [resolvedSrc, setResolvedSrc] = useState(null); // blob: URL we actually load
  const [loadError,   setLoadError]   = useState(null);
  const [imgDims,    setImgDims]    = useState(null); // { w, h } natural
  const [boxRect,    setBoxRect]    = useState(null); // image-display rect on screen
  const [crop,       setCrop]       = useState({ x: 0.04, y: 0.04, w: 0.92, h: 0.92 }); // fractions
  const [drag,       setDrag]       = useState(null); // { mode, startX, startY, startCrop }
  const [busy,       setBusy]       = useState(false);

  // Fetch the source as a blob and load via blob: URL. This sidesteps the
  // CORS issue with <img crossOrigin="anonymous"> against Storage URLs that
  // don't carry CORS headers consistently — fetch works because Storage
  // accepts the GET, and blob URLs are same-origin so canvas exports without
  // tainting issues. If src is already a blob: URL (e.g. from File picker),
  // we use it directly.
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setResolvedSrc(null);
    setLoadError(null);
    if (!src) return;
    if (src.startsWith("blob:") || src.startsWith("data:")) {
      setResolvedSrc(src);
      return;
    }
    (async () => {
      try {
        // Default cache mode — force-cache would serve a previously failed
        // (pre-CORS-config) response indefinitely.
        const res = await fetch(src);
        if (!res.ok) throw new Error(`Image fetch failed (${res.status})`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setResolvedSrc(createdUrl);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Couldn't load image.");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [src]);

  // Recalc the displayed image rect on resize / image load
  useEffect(() => {
    function recalc() {
      const img = imgRef.current;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      setBoxRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    }
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, [imgDims]);

  // Lock body scroll while open + Esc to cancel
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel]);

  function handleImgLoad(e) {
    setImgDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
  }

  // --- pointer handling ---
  function clientToFrac(clientX, clientY) {
    if (!boxRect) return { x: 0, y: 0 };
    return {
      x: (clientX - boxRect.left) / boxRect.width,
      y: (clientY - boxRect.top)  / boxRect.height,
    };
  }

  function startDrag(mode, e) {
    e.preventDefault();
    e.stopPropagation();
    const p = pointer(e);
    setDrag({
      mode,
      startX: p.x,
      startY: p.y,
      startCrop: { ...crop },
    });
  }

  useEffect(() => {
    if (!drag) return;
    function onMove(e) {
      const p = pointer(e);
      const f = clientToFrac(p.x, p.y);
      const sf = clientToFrac(drag.startX, drag.startY);
      const dx = f.x - sf.x;
      const dy = f.y - sf.y;
      let { x, y, w, h } = drag.startCrop;
      const min = 0.05;
      switch (drag.mode) {
        case "move":
          x = clamp(x + dx, 0, 1 - w);
          y = clamp(y + dy, 0, 1 - h);
          break;
        case "nw":
          { const newX = clamp(x + dx, 0, x + w - min);
            const newY = clamp(y + dy, 0, y + h - min);
            w = w + (x - newX); h = h + (y - newY); x = newX; y = newY;
          } break;
        case "ne":
          { const newY = clamp(y + dy, 0, y + h - min);
            w = clamp(w + dx, min, 1 - x);
            h = h + (y - newY); y = newY;
          } break;
        case "sw":
          { const newX = clamp(x + dx, 0, x + w - min);
            w = w + (x - newX); x = newX;
            h = clamp(h + dy, min, 1 - y);
          } break;
        case "se":
          w = clamp(w + dx, min, 1 - x);
          h = clamp(h + dy, min, 1 - y);
          break;
        case "n":
          { const newY = clamp(y + dy, 0, y + h - min);
            h = h + (y - newY); y = newY;
          } break;
        case "s":
          h = clamp(h + dy, min, 1 - y);
          break;
        case "w":
          { const newX = clamp(x + dx, 0, x + w - min);
            w = w + (x - newX); x = newX;
          } break;
        case "e":
          w = clamp(w + dx, min, 1 - x);
          break;
        default: break;
      }
      setCrop({ x, y, w, h });
    }
    function onEnd() { setDrag(null); }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
    };
  }, [drag]); // eslint-disable-line react-hooks/exhaustive-deps

  function reset() {
    setCrop({ x: 0.04, y: 0.04, w: 0.92, h: 0.92 });
  }

  async function confirm() {
    if (!imgDims || busy) return;
    setBusy(true);
    try {
      // Render the cropped region at native resolution to a canvas, export JPEG.
      const sx = Math.round(crop.x * imgDims.w);
      const sy = Math.round(crop.y * imgDims.h);
      const sw = Math.round(crop.w * imgDims.w);
      const sh = Math.round(crop.h * imgDims.h);
      const canvas = document.createElement("canvas");
      canvas.width  = sw;
      canvas.height = sh;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(imgRef.current, sx, sy, sw, sh, 0, 0, sw, sh);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      onConfirm?.(blob);
    } catch (err) {
      console.error("Pre-crop confirm failed", err);
      alert("Couldn't process the crop — try again.");
      setBusy(false);
    }
  }

  // Portal to document.body so the modal escapes any parent stacking context.
  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(6,12,25,0.92)",
      display: "flex", flexDirection: "column",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
    }}>
      {/* Header */}
      <div style={{
        padding: "0.9rem 1rem",
        borderBottom: `1px solid ${B.border}`,
        background: B.navy,
        display: "flex", alignItems: "center", gap: "0.85rem",
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: B.gold, fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.02em" }}>📐 {title}</div>
          {subtitle && <div style={{ color: B.muted, fontSize: "0.78rem", marginTop: "2px" }}>{subtitle}</div>}
        </div>
        <button onClick={onCancel} style={btnGhost}>Cancel</button>
      </div>

      {/* Image stage */}
      <div ref={containerRef} style={{
        flex: 1, position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem", overflow: "hidden",
        touchAction: "none",
      }}>
        <div style={{ position: "relative", maxWidth: "100%", maxHeight: "100%" }}>
          {loadError && (
            <div style={{ color: "#ff8a8a", fontSize: "0.9rem", padding: "1rem", textAlign: "center" }}>
              {loadError}
            </div>
          )}
          {!resolvedSrc && !loadError && (
            <div style={{ color: B.muted, fontSize: "0.85rem", padding: "1rem" }}>Loading…</div>
          )}
          {resolvedSrc && (
            <img
              ref={imgRef}
              src={resolvedSrc}
              alt=""
              onLoad={handleImgLoad}
              draggable={false}
              style={{
                display: "block",
                maxWidth: "min(100%, 92vw)",
                maxHeight: "calc(100vh - 230px)",
                userSelect: "none",
                touchAction: "none",
              }}
            />
          )}
          {boxRect && imgDims && resolvedSrc && (
            <CropOverlay
              crop={crop}
              boxRect={boxRect}
              startDrag={startDrag}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: "0.85rem 1rem",
        borderTop: `1px solid ${B.border}`,
        background: B.navy,
        display: "flex", alignItems: "center", gap: "0.6rem",
      }}>
        <button onClick={reset} style={btnGhost}>↺ Reset</button>
        <div style={{ flex: 1, color: B.muted, fontSize: "0.75rem", textAlign: "center" }}>
          Drag handles to resize · Drag inside to move
        </div>
        <button onClick={confirm} disabled={busy} style={{
          background: busy ? "rgba(239,83,126,0.4)" : B.pink,
          color: "white", border: "none", borderRadius: "10px",
          padding: "0.6rem 1.1rem", fontWeight: 800, fontSize: "0.9rem",
          cursor: busy ? "default" : "pointer",
          fontFamily: "inherit",
        }}>
          {busy ? "Cropping…" : "✓ Crop & continue"}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── CROP OVERLAY ─────────────────────────────────────────────────────────────
// Renders the dimmed mask + crop rectangle + handles. Positioned absolutely
// over the image. All measurements derived from boxRect (display size).

function CropOverlay({ crop, boxRect, startDrag }) {
  const w = boxRect.width;
  const h = boxRect.height;
  const left = crop.x * w;
  const top  = crop.y * h;
  const cw   = crop.w * w;
  const ch   = crop.h * h;

  const handleStyle = (cx, cy, cursor) => ({
    position: "absolute",
    left: cx - HANDLE_HIT / 2,
    top:  cy - HANDLE_HIT / 2,
    width:  HANDLE_HIT,
    height: HANDLE_HIT,
    cursor,
    touchAction: "none",
    zIndex: 3,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });
  const dotStyle = {
    width: HANDLE_VISUAL, height: HANDLE_VISUAL,
    borderRadius: "50%",
    background: B.gold,
    border: "2px solid white",
    boxShadow: "0 1px 6px rgba(0,0,0,0.55)",
    pointerEvents: "none",
  };

  return (
    <>
      {/* 4-edge dim mask using clip-path: outside crop = dark, inside = clear */}
      <div style={{
        position: "absolute", inset: 0,
        background: "rgba(8,12,25,0.72)",
        clipPath: `polygon(
          0 0, 100% 0, 100% 100%, 0 100%, 0 0,
          ${(crop.x*100).toFixed(3)}% ${(crop.y*100).toFixed(3)}%,
          ${(crop.x*100).toFixed(3)}% ${((crop.y+crop.h)*100).toFixed(3)}%,
          ${((crop.x+crop.w)*100).toFixed(3)}% ${((crop.y+crop.h)*100).toFixed(3)}%,
          ${((crop.x+crop.w)*100).toFixed(3)}% ${(crop.y*100).toFixed(3)}%,
          ${(crop.x*100).toFixed(3)}% ${(crop.y*100).toFixed(3)}%
        )`,
        pointerEvents: "none",
      }} />

      {/* Crop rectangle */}
      <div
        onPointerDown={(e) => startDrag("move", e)}
        style={{
          position: "absolute",
          left, top, width: cw, height: ch,
          border: `2px solid ${B.gold}`,
          boxShadow: "0 0 0 1px rgba(0,0,0,0.4)",
          cursor: "move",
          zIndex: 2,
          touchAction: "none",
          background: "transparent",
        }}
      >
        {/* Rule of thirds guide lines */}
        <div style={{ position: "absolute", left: "33.33%", top: 0, bottom: 0, width: "1px", background: "rgba(255,255,255,0.25)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: "66.66%", top: 0, bottom: 0, width: "1px", background: "rgba(255,255,255,0.25)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "33.33%", left: 0, right: 0, height: "1px", background: "rgba(255,255,255,0.25)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "66.66%", left: 0, right: 0, height: "1px", background: "rgba(255,255,255,0.25)", pointerEvents: "none" }} />
      </div>

      {/* Corner handles */}
      <div onPointerDown={(e) => startDrag("nw", e)} style={handleStyle(left,        top,         "nwse-resize")}><span style={dotStyle} /></div>
      <div onPointerDown={(e) => startDrag("ne", e)} style={handleStyle(left + cw,   top,         "nesw-resize")}><span style={dotStyle} /></div>
      <div onPointerDown={(e) => startDrag("sw", e)} style={handleStyle(left,        top + ch,    "nesw-resize")}><span style={dotStyle} /></div>
      <div onPointerDown={(e) => startDrag("se", e)} style={handleStyle(left + cw,   top + ch,    "nwse-resize")}><span style={dotStyle} /></div>

      {/* Edge midpoint handles */}
      <div onPointerDown={(e) => startDrag("n", e)}  style={handleStyle(left + cw/2, top,         "ns-resize")}><span style={dotStyle} /></div>
      <div onPointerDown={(e) => startDrag("s", e)}  style={handleStyle(left + cw/2, top + ch,    "ns-resize")}><span style={dotStyle} /></div>
      <div onPointerDown={(e) => startDrag("w", e)}  style={handleStyle(left,        top + ch/2,  "ew-resize")}><span style={dotStyle} /></div>
      <div onPointerDown={(e) => startDrag("e", e)}  style={handleStyle(left + cw,   top + ch/2,  "ew-resize")}><span style={dotStyle} /></div>
    </>
  );
}

// ── HELPERS ──────────────────────────────────────────────────────────────────

function pointer(e) {
  // Pointer events normalize touch + mouse — clientX/Y are always set.
  return { x: e.clientX, y: e.clientY };
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

const btnGhost = {
  background: "transparent",
  color: B.muted,
  border: `1px solid ${B.border}`,
  borderRadius: "8px",
  padding: "0.4rem 0.85rem",
  fontSize: "0.82rem",
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};
